/**
 * TRL (Trailblazers) Round Settings Form Component
 *
 * Provides the settings form for creating/editing Trailblazers rounds.
 * The admin picks one library screenshot for the round and tunes the search
 * duration and the sliding points scale. (Radar ranges — the capture/near
 * thresholds — are configured game-level in the map builder's Game Settings.)
 */

import { useState, useCallback, memo } from 'react';
import {
  TrlRoundSettings,
  GameType,
  Player,
  RoundPlayer,
  TrlRoundPlayer,
  DEFAULT_TRL_SETTINGS,
  DEFAULT_TRL_BASE_POINTS,
  DEFAULT_TRL_POINTS_DECREMENT,
} from '@swtorbit/shared-socket-events';
import { RoundFormBase } from '../round';
import { useTrlImageLibrary } from '../content-library';

interface TrlRoundSettingsFormProps {
  /** Available players to select from */
  players: Player[];
  /** Callback when form is submitted */
  onSubmit: (settings: TrlRoundSettings, players: RoundPlayer[]) => void;
  /** Callback when form is cancelled */
  onCancel: () => void;
  /** Initial values for editing (optional) */
  initialSettings?: TrlRoundSettings;
  /** Whether this is an edit form */
  isEditing?: boolean;
}

export const TrlRoundSettingsForm = memo(function TrlRoundSettingsForm({
  players,
  onSubmit,
  onCancel,
  initialSettings,
  isEditing = false,
}: TrlRoundSettingsFormProps) {
  const { images, imagesLoading } = useTrlImageLibrary();

  const [imageId, setImageId] = useState<string>(initialSettings?.imageId ?? '');
  const [searchDuration, setSearchDuration] = useState<number>(
    initialSettings?.searchDuration ?? DEFAULT_TRL_SETTINGS.searchDuration
  );
  const [basePoints, setBasePoints] = useState<number>(
    initialSettings?.basePoints ?? DEFAULT_TRL_BASE_POINTS
  );
  const [pointsDecrement, setPointsDecrement] = useState<number>(
    initialSettings?.pointsDecrement ?? DEFAULT_TRL_POINTS_DECREMENT
  );

  const handleSubmit = useCallback(() => {
    const chosen = images.find((img) => img.id === imageId);
    if (!chosen) return;

    const settings: TrlRoundSettings = {
      gameType: GameType.TRAILBLAZERS,
      imageId,
      // Populate the client-safe metadata so cards/overlays can render the
      // screenshot without the answer coordinates.
      imageUrl: chosen.url,
      imageName: chosen.name,
      imageArea: chosen.area,
      searchDuration,
      basePoints,
      pointsDecrement,
    };

    // All enabled players participate; TRL round players carry `hasCaptured`.
    const roundPlayers = players.map((player): TrlRoundPlayer => ({
      playerId: player.playerId,
      socketId: player.socketId,
      characterName: player.characterName || '',
      hasCaptured: false,
    }));

    onSubmit(settings, roundPlayers as RoundPlayer[]);
  }, [images, imageId, searchDuration, basePoints, pointsDecrement, players, onSubmit]);

  return (
    <RoundFormBase
      playerCount={players.length}
      isEditing={isEditing}
      submitDisabled={!imageId}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      extraClassName="trl-creation"
    >
      {/* Screenshot selection */}
      <div className="field">
        <label htmlFor="trl-image">Screenshot</label>
        <select
          id="trl-image"
          className="select"
          value={imageId}
          onChange={(e) => setImageId(e.target.value)}
        >
          <option value="">
            {imagesLoading ? 'Loading images…' : '-- Select a screenshot --'}
          </option>
          {images.map((img) => (
            <option key={img.id} value={img.id}>
              {img.name} — {img.area}
            </option>
          ))}
        </select>
        {images.length === 0 && !imagesLoading && (
          <p className="field__hint fs-xs">
            No screenshots in the library yet. Add one from Content Library (user menu).
          </p>
        )}
      </div>

      {/* Search duration */}
      <div className="field">
        <label htmlFor="trl-search-duration">Search duration (seconds, 0 = unlimited)</label>
        <input
          id="trl-search-duration"
          type="number"
          className="input"
          min={0}
          value={searchDuration}
          onChange={(e) => setSearchDuration(Math.max(0, Number(e.target.value)))}
        />
      </div>

      {/* Points */}
      <div className="d-flex gap-sm flex-wrap">
        <div className="field flex-1">
          <label htmlFor="trl-base-points">Base points (first capture)</label>
          <input
            id="trl-base-points"
            type="number"
            className="input"
            min={0}
            value={basePoints}
            onChange={(e) => setBasePoints(Math.max(0, Number(e.target.value)))}
          />
        </div>
        <div className="field flex-1">
          <label htmlFor="trl-points-decrement">Points decrement (per rank)</label>
          <input
            id="trl-points-decrement"
            type="number"
            className="input"
            min={0}
            value={pointsDecrement}
            onChange={(e) => setPointsDecrement(Math.max(0, Number(e.target.value)))}
          />
        </div>
      </div>

      <p className="field__hint fs-xs">
        The first team to dig within the winning radar range scores {basePoints} points; each
        subsequent team scores {pointsDecrement} fewer than the previous one.
      </p>
    </RoundFormBase>
  );
});
